"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Share2, Twitter, Facebook, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";

interface ShareButtonsProps {
  url: string;
  title?: string;
}

// Separate client component for Twitter sharing
function TwitterShareItem({ url, title }: { url: string; title: string }) {
  const shareToTwitter = () => {
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
    window.open(tweetUrl, '_blank', 'noopener,noreferrer');
  };
  
  return (
    <DropdownMenuItem onClick={shareToTwitter} className="flex items-center gap-2 cursor-pointer">
      <Twitter className="h-4 w-4 text-blue-400" />
      <span>Twitter</span>
    </DropdownMenuItem>
  );
}

// Separate client component for Facebook sharing
function FacebookShareItem({ url }: { url: string }) {
  const shareToFacebook = () => {
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(fbUrl, '_blank', 'noopener,noreferrer');
  };
  
  return (
    <DropdownMenuItem onClick={shareToFacebook} className="flex items-center gap-2 cursor-pointer">
      <Facebook className="h-4 w-4 text-blue-600" />
      <span>Facebook</span>
    </DropdownMenuItem>
  );
}

// Separate client component for copying links
function CopyLinkItem({ url }: { url: string }) {
  const copyLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy link");
      console.error("Error copying link: ", err);
    }
  };
  
  return (
    <DropdownMenuItem onClick={copyLinkToClipboard} className="flex items-center gap-2 cursor-pointer">
      <LinkIcon className="h-4 w-4" />
      <span>Copy Link</span>
    </DropdownMenuItem>
  );
}

export function ShareButtons({ url, title = "Check out this blog post" }: ShareButtonsProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Ensure URL is absolute
  const fullUrl = url.startsWith('http') 
    ? url 
    : `${process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '')}${url}`;
  

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
          aria-label="Share this post"
        >
          <Share2 className="h-3.5 w-3.5" />
          <span className="sr-only md:not-sr-only md:inline-block">Share</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <TwitterShareItem url={fullUrl} title={title} />
        <FacebookShareItem url={fullUrl} />
        <CopyLinkItem url={fullUrl} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
